import { Target, Eye, Users, Award, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SEO, breadcrumbJsonLd } from "@/lib/seo";

const About = () => {
    const { t } = useTranslation();

    const values = [
        { title: t("about.values.innovation_title"), desc: t("about.values.innovation_desc") },
        { title: t("about.values.passion_title"), desc: t("about.values.passion_desc") },
        { title: t("about.values.quality_title"), desc: t("about.values.quality_desc") },
        { title: t("about.values.commitment_title"), desc: t("about.values.commitment_desc") },
    ];

    const stats = [
        { number: "48+", label: t("about.stat_projects") },
        { number: "7+", label: t("about.stat_years") },
        { number: "31+", label: t("about.stat_partners") },
        { number: "24/7", label: t("about.stat_support") },
    ];

    const why = [
        { t: t("about.why.transparent_t"), d: t("about.why.transparent_d") },
        { t: t("about.why.support_t"), d: t("about.why.support_d") },
        { t: t("about.why.tech_t"), d: t("about.why.tech_d") },
    ];

    return (
        <>
            <SEO
                title={t("meta.about_title")}
                description={t("meta.about_description")}
                path="/about"
                jsonLd={breadcrumbJsonLd([
                    { name: t("nav.home"), path: "/" },
                    { name: t("nav.about"), path: "/about" },
                ])}
            />
            <div className="pt-28 sm:pt-32 pb-16 sm:pb-24">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mb-32">
                        <h1
                            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black text-white mb-10 tracking-normal animate-fade-in"
                            style={{ lineHeight: '1.6' }}
                        >
                            {t("about.title_prefix")} <br />
                            <span className="text-muted-foreground text-3xl sm:text-4xl md:text-6xl lg:text-7xl">{t("about.title_highlight")}</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-muted-foreground font-medium leading-relaxed max-w-3xl">
                            {t("about.lead")}
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 mb-40">
                        <div className="bento-card p-6 sm:p-10 md:p-16 group">
                            <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-white mb-8 group-hover:bg-primary transition-all">
                                <Eye size={24} />
                            </div>
                            <h2 className="text-4xl font-black text-white mb-6">{t("about.vision_title")}</h2>
                            <p className="text-lg text-muted-foreground font-medium leading-relaxed">
                                {t("about.vision_body")}
                            </p>
                        </div>
                        <div className="bento-card p-6 sm:p-10 md:p-16 group">
                            <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-white mb-8 group-hover:bg-primary transition-all">
                                <Target size={24} />
                            </div>
                            <h2 className="text-4xl font-black text-white mb-6">{t("about.mission_title")}</h2>
                            <p className="text-lg text-muted-foreground font-medium leading-relaxed">
                                {t("about.mission_body")}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 mb-40">
                        {stats.map((stat, i) => (
                            <div key={i} className="text-center">
                                <p className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-2 tracking-tighter">{stat.number}</p>
                                <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest">{stat.label}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mb-40">
                        <h2 className="text-4xl md:text-6xl font-black text-white mb-20 tracking-tighter">{t("about.values_title")}</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {values.map((v, i) => (
                                <div key={i} className="bento-card p-10 group">
                                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-primary transition-colors">{v.title}</h3>
                                    <p className="text-muted-foreground font-medium leading-relaxed">
                                        {v.desc}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-20 items-center mb-40">
                        <div>
                            <h2 className="text-4xl md:text-6xl font-black text-white mb-10 tracking-tighter">{t("about.why_title")}</h2>
                            <div className="space-y-8">
                                {why.map((item, i) => (
                                    <div key={i} className="flex gap-6">
                                        <div className="mt-1 text-primary">
                                            <CheckCircle2 size={24} />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-white mb-2">{item.t}</h3>
                                            <p className="text-muted-foreground font-medium">{item.d}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="aspect-[4/5] bg-white/[0.02] border border-white/5 rounded-[4rem] flex items-center justify-center relative overflow-hidden">
                            <div className="absolute inset-0 bg-grid opacity-10" />
                            <Award size={120} className="text-white/5" />
                            <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex items-center gap-3 px-6 py-3 rounded-full bg-white text-background font-black text-sm shadow-tech">
                                <Users size={16} />
                                {t("about.team_badge")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default About;
